import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getBaseUrl } from "./utils";
import type { RootState } from "./store";

export const api = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: getBaseUrl(),
    credentials: "include",
    prepareHeaders: async (headers, { getState }) => {
      // Prefer fetching a fresh token from the Auth0 SDK at request time.
      // The SDK's getAccessTokenSilently has its own in-memory cache: it
      // returns the cached token if still valid (microseconds) or
      // transparently uses the refresh token to mint a new one if not
      // (one network roundtrip). Asking on every request eliminates the
      // gap between periodic refreshes that previously let expired tokens
      // leak to the backend as 401s.
      //
      // Fall back to the Redux-cached token when window.getFreshAccessToken
      // isn't set yet (cold start, before Auth0Provider has mounted) or
      // when the SDK throws (refresh token gone). Sending a stale or
      // missing token here means the request 401s and the 401 middleware
      // handler routes through forceReauth — same end state as today.
      let token: string | undefined;
      if (typeof window.getFreshAccessToken === "function") {
        try {
          token = await window.getFreshAccessToken();
        } catch {
          // SDK couldn't mint a fresh token; fall back to whatever's in
          // Redux. If that's stale too, the resulting 401 triggers the
          // existing reauth flow.
        }
      }
      if (!token) {
        token = (getState() as RootState).main.accessToken ?? undefined;
      }
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: [
    "Clients",
    "DOI",
    "FileAdjuster",
    "RRE",
    "RREs",
    "RRETins",
    "RRETinValidations",
    "Claim",
    "Claims",
    "Section111ClaimantMatches",
    "ClaimDocuments",
    "ClaimAuditHistory",
    "Claimants",
    "ClaimValidations",
    "RREPolicies",
    "RREPolicy",
    "RREImportLogs",
    "RREValidations",
    "File",
    "Files",
    "FileValidation",
    "RRETags",
    "RRETagById",
    "RREDocuments",
    "MSA",
    "MSADecision",
    "MSATable",
    "MSAValidation",
    "ClientSubmission",
    "TreatmentAllocation",
    "PrescriptionAllocation",
    "ServiceAssignee",
    "Documents",
    "PlanCoverage",
    "PartABPlanCoverage",
    "PartABPlanCoverageById",
    "PartCDPlanCoverage",
    "PartCDPlanCoverageById",
    "Employers",
    "Lien",
    "Liens",
    "LienFileValidation",
    "LienPaymentHistory",
    "LienFile",
    "LienValidation",
    "Tasks",
    "Client",
    "MedicalPayHistory",
    "RxPayHistory",
    "MCP",
    "LCP",
    "CostProjectionTable",
    "CostProjectionValidation",
    "AmendedScreeningTable",
    "AmendedScreeningValidation",
    "Invoice",
    "Adjuster",
    "Adjusters",
    "Invoices",
    "InvoiceLineItem",
    "MiscService",
    "AmendedReviewScreening",
    "AmendedReviewScreeningValidation",
    "ClientInstructions",
    "PaymentHistories",
    "MSAReReview",
    "MedicalRecordIndexes",
    "ReportPrescriptionAllocation",
    "ReportTreatmentAllocation",
    "Users",
    "QuickbooksWorkspace",
    "TeamBandWidth",
    "IntakeUsers",
    "RequestToVacate",
    "RequestToReopen",
    "LienDirectory",
    "ServiceDateOfInjury",
    "OrganizationSettings",
    "SuggestedClaimLinks",
    "BackgroundJobs",
    "SurgicalPricingCalculation",
    "Service",
    "ArchivedServices",
    "DocumentManagementFiles",
    "DocumentManagementFileValidation",
    "TPA",
    "Insurer",
    "FinanceReport",
    "SavedViews",
    "SavedView",
    "Notes",
    "Note",
    "RatedAge",
    "BulkDownloads",
    "CmsSubmissionPackages",
    "Metadocs",
    "MetadocMetadata",
    "DocumentGroups",
    "DocumentGroupTags",
    "TextractRequest",
  ],
  endpoints: (build) => ({
    generateReport: build.mutation<unknown, { data: unknown }>({
      query: ({ data }) => {
        return {
          url: `api/v1/reporting/section111/`,
          method: "POST",
          body: data,
        };
      },
    }),
    generateOpsReport: build.mutation<unknown, { data: unknown }>({
      query: ({ data }) => {
        return {
          url: `api/v1/ops-reporting/generate/`,
          method: "POST",
          body: data,
        };
      },
    }),

    getImportTemplate: build.query<
      string,
      { fileName: string; rreId: null | number; url: string }
    >({
      query: ({ url, rreId }) => {
        const apiUrl = rreId
          ? `api/v1/importexport/${url}-template/?rre_id=${rreId}`
          : `api/v1/importexport/${url}-template/`;
        return {
          url: apiUrl,
          method: "GET",
          responseHandler: "text",
        };
      },
      async onQueryStarted({ fileName }, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const blob = new Blob([data], { type: "text/plain" });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        } catch (error) {
          console.error("Failed to download the file:", error);
        }
      },
    }),

    getAdjusterImportTemplate: build.query<
      string,
      { fileName: string; rreId: null | number; url: string }
    >({
      query: ({ rreId }) => {
        const apiUrl = `api/v1/importexport/adjuster-template/?rre_id=${rreId}`;
        return {
          url: apiUrl,
          method: "GET",
          responseHandler: "text",
        };
      },
      async onQueryStarted({ fileName }, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const blob = new Blob([data], { type: "text/plain" });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        } catch (error) {
          console.error("Failed to download the file:", error);
        }
      },
    }),

    getSection111Analytics: build.query<
      unknown,
      { rreIds: string[]; dataFormat?: string }
    >({
      query: ({ rreIds, dataFormat = "COUNT" }) => {
        const params = new URLSearchParams();
        rreIds.forEach((id) => params.append("rre_ids", id));
        params.set("data_format", dataFormat);
        return {
          url: `api/v1/analytics/`,
          method: "GET",
          params: params,
        };
      },
      providesTags: [],
    }),
  }),
});

export const {
  useGenerateReportMutation,
  useGenerateOpsReportMutation,
  useLazyGetImportTemplateQuery,
  useLazyGetAdjusterImportTemplateQuery,
  useGetSection111AnalyticsQuery,
} = api;
